import React, { useState } from 'react';
import { format } from 'date-fns';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import StatusEditorPopover from './StatusEditorPopover';

// Types can be reused from the page; duplicated here to avoid circular deps
interface BoardMeta {
  id: string;
  title: string;
  description?: string;
  createdAt: number;
  lastModified?: number;
  statuses?: string[];
}

interface BoardCardProps {
  board: BoardMeta;
  onClick: () => void;
  teamId: string;
}

const gradientOptions = [
  'from-pink-500 to-yellow-500',
  'from-purple-500 to-indigo-500',
  'from-green-500 to-emerald-400',
  'from-blue-500 to-purple-500',
  'from-orange-500 to-red-500',
];

const BoardCard: React.FC<BoardCardProps> = ({ board, onClick, teamId }) => {
  const [editing, setEditing] = useState(false);
  // Pick a gradient in a deterministic way based on the board id
  const gradient = gradientOptions[Math.abs(hashString(board.id)) % gradientOptions.length];
  const created = format(new Date(board.createdAt), 'PP');
  const updated = board.lastModified ? format(new Date(board.lastModified), 'PP') : null;

  return (
    <div
      className="relative group cursor-pointer bg-white/70 backdrop-blur-sm border border-gray-200 rounded-xl p-4 flex gap-4 hover:shadow-xl hover:-translate-y-1 hover:scale-[1.02] transition-all duration-200"
      onClick={editing ? undefined : onClick}
    >
      {/* Edit icon */}
      <PencilSquareIcon
        className="absolute top-2 right-2 w-4 h-4 text-gray-500 hover:text-gray-700"
        onClick={(e)=>{e.stopPropagation(); setEditing((p)=>!p);}}
      />
      {editing && (
        <StatusEditorPopover
          teamId={teamId}
          boardId={board.id}
          initialStatuses={board.statuses || ['active']}
          onClose={()=>setEditing(false)}
        />
      )}
      {/* Thumbnail */}
      <div
        className={`flex-shrink-0 w-16 h-16 rounded-md bg-gradient-to-br ${gradient} text-white font-bold text-2xl flex items-center justify-center`}
      >
        {board.title.charAt(0).toUpperCase()}
      </div>

      {/* Content */}
      <div className="flex flex-col min-w-0">
        <h3 className="font-semibold text-lg mb-1 truncate max-w-full" title={board.title}>
          {board.title}
        </h3>
        {/* Status badges */}
        {board.statuses && (
          <div className="flex gap-1 mb-1">
            {board.statuses.map((s) => (
              <span key={s} className={`text-[10px] px-2 py-0.5 rounded-full ${badgeClass(s)}`}>{capitalize(s)}</span>
            ))}
          </div>
        )}
        {board.description && (
          <p className="text-sm text-gray-600 line-clamp-2 mb-2">{board.description}</p>
        )}
        <span className="text-xs text-gray-500">
          Created {created}
          {updated && <> <span className="mx-1">·</span>Updated {updated}</>}
        </span>
      </div>
    </div>
  );
};

// Simple hash to pick a gradient index
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
}

function badgeClass(status: string) {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-800';
    case 'archived':
      return 'bg-gray-200 text-gray-700';
    default:
      return 'bg-blue-100 text-blue-800';
  }
}

function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export default BoardCard; 